import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement, BeamElement, BIMElement } from '../../core/types';

/**
 * Mirror - Draw Axis Tool - BIM 2025 Style
 * 
 * Mirrors selected elements by drawing a custom axis line.
 * Unlike Mirror - Pick Axis (which uses existing geometry), this draws a new line.
 * 
 * Supports:
 * - Copy mode (creates mirrored copies)
 * - Custom axis via two points
 * 
 * Workflow:
 * 1. Select elements (or pre-select)
 * 2. Click first point of mirror axis
 * 3. Click second point of mirror axis
 */
export class MirrorDrawToolClass extends BaseTool {
    id = 'MIRROR_DRAW';
    name = 'Mirror - Draw Axis';
    cursor = 'crosshair';

    private axisStart: THREE.Vector3 | null = null;
    private axisEnd: THREE.Vector3 | null = null;
    private copyMode: boolean = true; // Default: create copies, not move
    private selectedElements: BIMElement[] = [];

    activate(context: any): void {
        super.activate(context);
        this.loadSelection();

        if (this.selectedElements.length === 0) {
            console.log('Mirror Draw: No elements selected. Click to select elements first.');
        } else {
            console.log('Mirror Draw: Selected', this.selectedElements.length, 'elements. Draw mirror axis.');
        }
    }

    private loadSelection(): void {
        if (!this.context) return;

        const selectedIds = this.context.appStore.selectedIds || [];
        this.selectedElements = [];

        for (const id of selectedIds) {
            const element = this.context.bimStore.project.elements.get(id);
            if (element) {
                this.selectedElements.push(element);
            }
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        // If no selection, try to select clicked element
        if (this.selectedElements.length === 0) {
            const id = this.findElementIdAtPoint(event);
            if (id) {
                this.selectElement(id);
                this.loadSelection();
            }
            return;
        }

        if (!this.axisStart) {
            // First click: Start of mirror axis
            this.axisStart = snappedPoint.clone();
            console.log('Mirror Draw: Axis start set', this.axisStart);

            this.context.appStore.setToolPreview({
                active: true,
                toolId: this.id,
                axisStart: this.axisStart
            });
        } else {
            // Second click: End of mirror axis
            this.axisEnd = snappedPoint.clone();
            console.log('Mirror Draw: Axis end set', this.axisEnd);

            // Execute mirror
            this.mirrorElements();

            // Reset and exit
            this.reset();
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.axisStart) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const currentPoint = this.snapToGrid(point);

        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            axisStart: this.axisStart,
            axisEnd: currentPoint,
            copyMode: this.copyMode
        });
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        // Toggle copy mode with Ctrl
        if (event.key === 'Control') {
            this.copyMode = !this.copyMode;
            console.log('Mirror Draw: Copy mode', this.copyMode ? 'ON' : 'OFF');
        }

        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    private mirrorElements(): void {
        if (!this.context || !this.axisStart || !this.axisEnd) return;

        const newIds: string[] = [];

        for (const element of this.selectedElements) {
            const result = this.mirrorElement(element);
            if (result) {
                if (this.copyMode) {
                    newIds.push(result);
                }
            }
        }

        if (this.copyMode && newIds.length > 0) {
            this.context.appStore.select(newIds);
        }

        console.log('Mirror Draw:', this.copyMode ? 'Created' : 'Moved',
            this.selectedElements.length, 'mirrored elements');
    }

    private mirrorElement(element: BIMElement): string | null {
        if (!this.context) return null;

        switch (element.category) {
            case 'Walls':
                return this.mirrorWall(element as WallElement);
            case 'Structural Framing':
                return this.mirrorBeam(element as BeamElement);
            default:
                return this.mirrorGenericElement(element);
        }
    }

    private mirrorPoint(point: THREE.Vector3): THREE.Vector3 {
        if (!this.axisStart || !this.axisEnd) return point.clone();

        // Mirror a point across the axis line
        // Formula: P' = 2 * projection of P onto axis - P

        const axisDir = this.axisEnd.clone().sub(this.axisStart).normalize();
        const toPoint = point.clone().sub(this.axisStart);

        // Project point onto axis line
        const projLength = toPoint.dot(axisDir);
        const projection = this.axisStart.clone().add(axisDir.clone().multiplyScalar(projLength));

        // Mirror = 2 * projection - original
        const mirrored = projection.clone().multiplyScalar(2).sub(point);

        return mirrored;
    }

    private mirrorWall(wall: WallElement): string | null {
        const start = new THREE.Vector3(...wall.geometryData.start);
        const end = new THREE.Vector3(...wall.geometryData.end);

        const newStart = this.mirrorPoint(start);
        const newEnd = this.mirrorPoint(end);

        const newGeometry = {
            ...wall.geometryData,
            start: [newStart.x, newStart.y, newStart.z] as [number, number, number],
            end: [newEnd.x, newEnd.y, newEnd.z] as [number, number, number]
        };

        if (this.copyMode) {
            const newWall: WallElement = {
                ...wall,
                id: THREE.MathUtils.generateUUID(),
                geometryData: newGeometry,
                constraints: []
            };
            this.context!.bimStore.addElement(newWall);
            return newWall.id;
        } else {
            this.context!.bimStore.updateElement(wall.id, { geometryData: newGeometry });
            return wall.id;
        }
    }

    private mirrorBeam(beam: BeamElement): string | null {
        const start = new THREE.Vector3(...beam.geometryData.start);
        const end = new THREE.Vector3(...beam.geometryData.end);

        const newStart = this.mirrorPoint(start);
        const newEnd = this.mirrorPoint(end);

        const newGeometry = {
            ...beam.geometryData,
            start: [newStart.x, newStart.y, newStart.z] as [number, number, number],
            end: [newEnd.x, newEnd.y, newEnd.z] as [number, number, number]
        };

        if (this.copyMode) {
            const newBeam: BeamElement = {
                ...beam,
                id: THREE.MathUtils.generateUUID(),
                geometryData: newGeometry,
                constraints: []
            };
            this.context!.bimStore.addElement(newBeam);
            return newBeam.id;
        } else {
            this.context!.bimStore.updateElement(beam.id, { geometryData: newGeometry });
            return beam.id;
        }
    }

    private mirrorGenericElement(element: BIMElement): string | null {
        // For generic elements without specific geometry, just create a copy
        if (this.copyMode) {
            const newElement = {
                ...element,
                id: THREE.MathUtils.generateUUID(),
                constraints: []
            };
            this.context!.bimStore.addElement(newElement);
            return newElement.id;
        }
        return element.id;
    }

    private reset(): void {
        this.axisStart = null;
        this.axisEnd = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        super.deactivate();
        this.reset();
        this.selectedElements = [];
    }
}
